import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import {
  parseFormSchema,
  type FormField as SchemaField,
  type VisibleWhen,
} from "@/lib/formSchema";
import { dealsApi } from "@/api/deals";
import { finalFinancialAddendumApi } from "@/api/dealFlow/client";
import { covenantCalibrationApi } from "@/api/portfolioAdmin/covenantCalibrationApi";
import { collectionPackagesApi } from "@/api/collectionPackages";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/design-system/lib/format";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import {
  balloonDueDate,
  computeLoanFigures,
  monthsToTermLabel,
} from "@/lib/amortization";
import { senderDetailsKeys } from "./senderDetailsKeys";

// Sender-Fields v1 — composer step for templates that carry sender fields
// (a letter/document with {{tokens}} filled by staff, not the recipient).
// Resolves each field by valueSource: System (auto), DealField (prefilled from
// the bound deal/addendum, staff-overridable), Computed (derived, read-only),
// Manual (typed). Emits the captured values as a JSON blob and renders a live
// server-rendered PDF preview beside the form (preview == the signed letter).

export type SenderValue = string | number | null;
export type SenderValues = Record<string, SenderValue>;

interface Props {
  templateId: string;
  templateName: string;
  formSchemaJson: string | null;
  /** Owning deal id when the package is filed against a Deal (enables
   *  DealField prefill). Null otherwise — DealField values stay blank. */
  dealId: string | null;
  recipientName: string | null;
  staffName: string | null;
  staffEmail: string | null;
  /** Reports the captured values as a JSON string for the item create. */
  onValuesChange: (templateId: string, valuesJson: string) => void;
  /** Special Forms (send popup) — server-resolved prefill seeded as the
   *  INITIAL editable values (e.g. party.name/email/state from the
   *  special-form definition endpoint, which the client-side deal/addendum
   *  resolvers can't produce). The once-only seeding effects respect
   *  already-present values, so these survive; staff can still edit. */
  initialValues?: SenderValues;
  /** Special forms — a composing SpecialFormEditor renders its own preview
   *  (which carries the body override); hide the built-in one. */
  hidePreview?: boolean;
  /** Owner Recapitalization (composer_notes 3, 5) — keys whose value is shared
   *  with the other Conditional Approval letter in the same send: a staff edit of
   *  one of them here is reported through `onSyncedValueChange`, and a value the
   *  parent passes in `syncedValues` is applied here. Omitted → no sharing. */
  syncKeys?: readonly string[];
  syncedValues?: SenderValues;
  onSyncedValueChange?: (name: string, value: SenderValue) => void;
}

/** Today in the VIEWER's zone as `yyyy-MM-dd`. The UTC day is a different
 *  day from 17:00 Phoenix, and this seeds the printed LetterDate. */
const todayIso = (tz: string) => isoDateInTz(new Date(), tz);

/** Does this template have any sender fields? Drives whether the composer
 *  renders a Sender-details step at all. */
export function templateHasSenderFields(formSchemaJson: string | null): boolean {
  const schema = parseFormSchema(formSchemaJson);
  return !!schema?.fields.some((f) => f.filledBy === "Sender");
}

// Financial Addendum — the 16 covenant Calibrated Amounts, keyed by the stable
// Code on LoanCovenantCalibrationAmount (CovenantCalibrationSeedData). Tokens
// prefill from the loan's real calibration (covenantCalibrationApi) by Code;
// these fixed starting defaults — identical to the seed — are the fallback when
// the deal has no loan yet (pre-close) or a code is missing.
const CALIBRATION_DEFAULTS: Record<string, number> = {
  min_working_capital: 10000,
  min_annual_noi: 50000,
  min_tangible_net_worth: 10000,
  pm_equipment_fin_per_occurrence: 25000,
  pm_equipment_fin_annual_aggregate: 50000,
  revolving_wc_line_outstanding: 50000,
  asset_disposal_per_item: 5000,
  asset_disposal_annual_aggregate: 10000,
  employee_advance_per_employee: 5000,
  employee_advance_aggregate: 25000,
  capex_annual_limit: 25000,
  cross_default_other_indebtedness: 50000,
  money_judgment: 50000,
  collateral_impairment_fmv: 25000,
  litigation_claims_notice: 25000,
  collateral_loss_damage_notice: 10000,
};

/** ISO timestamp → yyyy-MM-dd (date-only), or null. */
function dateOnly(v: string | null | undefined): string | null {
  return v ? v.slice(0, 10) : null;
}

function resolveDealToken(
  token: string | undefined,
  deal: Awaited<ReturnType<typeof dealsApi.get>> | undefined,
  addendum: Awaited<ReturnType<typeof finalFinancialAddendumApi.get>> | undefined | null,
  calibration?: Awaited<ReturnType<typeof covenantCalibrationApi.get>> | null,
): SenderValue {
  if (!token) return null;
  // Financial Addendum covenant Calibrated Amounts — the loan's real value by
  // Code, else the fixed seed default (pre-close / missing code).
  if (token.startsWith("calibration.")) {
    const code = token.slice("calibration.".length);
    const found = calibration?.amounts.find((a) => a.code === code);
    return found?.amount ?? CALIBRATION_DEFAULTS[code] ?? null;
  }
  switch (token) {
    case "deal.businessNameInitial":
      return deal?.businessNameInitial ?? null;
    case "deal.dealNumber":
      return deal?.dealNumber ?? null;
    case "deal.purchasePrice":
      return deal?.purchasePrice ?? null;
    case "deal.sellerNoteAmount":
      return deal?.sellerNoteAmount ?? null;
    case "deal.loanNumber":
      return deal?.loanNumber ?? null;
    case "deal.cashAtClosing":
      return deal?.cashAtClosing ?? null;
    case "deal.closingDate":
      // The deal DTO carries no escrow ReleasedAt; closedAt (stamped at
      // Create-Loan) is the close date, falling back to the target close date.
      // Staff confirm / override the authoritative Closing Date.
      return dateOnly(deal?.closedAt ?? deal?.targetCloseDate);
    case "addendum.loanAmount":
      return addendum?.loanAmount ?? null;
    case "addendum.interestRatePct":
      // Stored as a decimal fraction (0.075 = 7.5%); the letter's percentage
      // field expects a percent value.
      return addendum?.interestRatePct != null ? addendum.interestRatePct * 100 : null;
    default:
      return null;
  }
}

function isComputed(f: SchemaField) {
  return f.valueSource === "Computed";
}
function isEditable(f: SchemaField) {
  return f.valueSource === "Manual" || f.valueSource === "DealField";
}

function asNumber(v: SenderValue): number {
  return typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : 0;
}

function hasVal(v: SenderValue | undefined): boolean {
  return v != null && v !== "";
}

// Financial Addendum — fixed program terms (also stated verbatim in the body).
// Used to derive the computed P&I / balloon / maturity when the template has no
// staff-editable term fields (the pre-approval letter, by contrast, captures its
// terms as fields). Scoped to the addendum via its BuyerNotePrincipal field so
// other sender templates are unaffected.
const ADDENDUM_RATE_PCT = 10;
const ADDENDUM_AMORT_MONTHS = 240;
const ADDENDUM_BALLOON_MONTHS = 96;

// Conditional Approval letters (Appendix D) — the composer preview of
// SenderLetterFigureComputer.ComputeConditionalApprovalFigures. The server value
// is authoritative and overwrites these at persist and preview; both are pinned to
// the cent by tests/fixtures/conditional-approval-letter-figures.json, which also
// carries these defaults (the server reads them from PrequalProgramTerms: the
// 1 November 2026 program terms). Detected by the BaseFinancedAmount field.
export const CONDITIONAL_APPROVAL_DEFAULTS = {
  OnboardingFeeRate: 4.95,
  PaymentAdministrationFeeRate: 1.95,
  UnderwritingFeeRate: 2.95,
  BalloonTermMonths: 96,
  TwentyYearAmortizationMonths: 240,
  TenYearAmortizationMonths: 120,
} as const;

/** Round to the cent, halves away from zero, on the DECIMAL value: 20.005 is
 *  stored in binary as 20.00499…, so the product is first trimmed to 15
 *  significant digits (the server rounds a true decimal). */
function quantizeCents(value: number): number {
  const scaled = Number((Math.abs(value) * 100).toPrecision(15));
  return (Math.sign(value) * Math.round(scaled)) / 100;
}

export interface ConditionalApprovalInputs {
  BaseFinancedAmount: number | null;
  InterestRate: number | null;
  OnboardingFeeRate: number | null;
  PaymentAdministrationFeeRate: number | null;
  UnderwritingFeeRate: number | null;
  BalloonTermMonths: number | null;
  /** Owner Recapitalization, Option E letters — read by `computeRecapLetterFigures`. */
  UnderwritingFeeCollectedRate?: number | null;
}

export interface ConditionalApprovalFigures {
  OnboardingFeeRate: number;
  PaymentAdministrationFeeRate: number;
  UnderwritingFeeRate: number;
  BalloonTermMonths: number;
  OnboardingFee: number | null;
  BuyerNotePrincipal: number | null;
  MonthlyPayment20: number | null;
  MonthlyPayment10: number | null;
  BalloonPayment20: number | null;
  BalloonPayment10: number | null;
  PaymentAdministrationFee20: number | null;
  PaymentAdministrationFee10: number | null;
  MonthlyDebit20: number | null;
  MonthlyDebit10: number | null;
  UnderwritingFee: number | null;
}

/** The level monthly payment BEFORE rounding (the balloon is fed this, not the
 *  rounded payment). `annualRatePct` is a percent; zero amortizes straight-line. */
function unroundedPayment(principal: number, annualRatePct: number, months: number): number {
  const r = annualRatePct / 100 / 12;
  if (!(r > 0)) return principal / months;
  const growth = Math.pow(1 + r, months);
  return principal * ((r * growth) / (growth - 1));
}

/** Balance after `term` period-end payments of the UNROUNDED payment, rounded to
 *  the cent and floored at zero — the closed form every before-closing calculator
 *  uses (program documents of 1 November 2026; ruling Q3's rounded-payment
 *  balloon is retired). */
function closedFormBalloon(principal: number, annualRatePct: number, rawPayment: number, term: number): number {
  const r = annualRatePct / 100 / 12;
  if (!(r > 0)) return quantizeCents(Math.max(0, principal - rawPayment * term));
  const growth = Math.pow(1 + r, term);
  return quantizeCents(Math.max(0, principal * growth - rawPayment * ((growth - 1) / r)));
}

/** A rate outside 0..100 (or blank) is not a rate; it resolves to its default. */
function rateOrDefault(rate: number | null, fallback: number): number {
  return rate != null && Number.isFinite(rate) && rate >= 0 && rate <= 100 ? rate : fallback;
}

export function computeConditionalApprovalFigures(
  input: ConditionalApprovalInputs,
): ConditionalApprovalFigures {
  const d = CONDITIONAL_APPROVAL_DEFAULTS;
  const onboardingRate = rateOrDefault(input.OnboardingFeeRate, d.OnboardingFeeRate);
  const pafRate = rateOrDefault(input.PaymentAdministrationFeeRate, d.PaymentAdministrationFeeRate);
  const uwRate = rateOrDefault(input.UnderwritingFeeRate, d.UnderwritingFeeRate);
  const t = input.BalloonTermMonths;
  const term = t != null && Number.isFinite(t) && t >= 1 && t <= 1200 ? Math.trunc(t) : d.BalloonTermMonths;
  const resolved = {
    OnboardingFeeRate: onboardingRate,
    PaymentAdministrationFeeRate: pafRate,
    UnderwritingFeeRate: uwRate,
    BalloonTermMonths: term,
  };

  const rawBase = input.BaseFinancedAmount;
  if (rawBase == null || !Number.isFinite(rawBase) || !(rawBase > 0)) {
    return {
      ...resolved,
      OnboardingFee: null,
      BuyerNotePrincipal: null,
      MonthlyPayment20: null,
      MonthlyPayment10: null,
      BalloonPayment20: null,
      BalloonPayment10: null,
      PaymentAdministrationFee20: null,
      PaymentAdministrationFee10: null,
      MonthlyDebit20: null,
      MonthlyDebit10: null,
      UnderwritingFee: null,
    };
  }

  const base = quantizeCents(rawBase);
  const ratePct = input.InterestRate ?? 0;
  const onboardingFee = quantizeCents((base * onboardingRate) / 100);
  const principal = quantizeCents(base + onboardingFee);
  const raw20 = unroundedPayment(principal, ratePct, d.TwentyYearAmortizationMonths);
  const raw10 = unroundedPayment(principal, ratePct, d.TenYearAmortizationMonths);
  const payment20 = quantizeCents(raw20);
  const payment10 = quantizeCents(raw10);
  const paf20 = quantizeCents((payment20 * pafRate) / 100);
  const paf10 = quantizeCents((payment10 * pafRate) / 100);
  return {
    ...resolved,
    OnboardingFee: onboardingFee,
    BuyerNotePrincipal: principal,
    MonthlyPayment20: payment20,
    MonthlyPayment10: payment10,
    // The balloon is fed the UNROUNDED payment, as on the server.
    BalloonPayment20: closedFormBalloon(principal, ratePct, raw20, term),
    BalloonPayment10: closedFormBalloon(principal, ratePct, raw10, term),
    PaymentAdministrationFee20: paf20,
    PaymentAdministrationFee10: paf10,
    MonthlyDebit20: quantizeCents(payment20 + paf20),
    MonthlyDebit10: quantizeCents(payment10 + paf10),
    UnderwritingFee: quantizeCents((base * uwRate) / 100),
  };
}

// Owner Recapitalization (letters.json tokens_new) — the recap letters' extra
// figures, mirroring SenderLetterFigureComputer (the server value is
// authoritative). Pinned to the cent by the fixture's `recapCases`. The collected
// rate defaults to 1.00% (Seller Participation Agreement §4.3A(a)); the first
// month's Pre-Contribution Administration Fee is the Buyer Note Principal × 5% ÷ 12.
export const RECAP_LETTER_DEFAULTS = {
  UnderwritingFeeCollectedRate: 1.0,
  PreContributionAdministrationFeeAnnualPct: 5,
} as const;

export interface RecapLetterFigures extends ConditionalApprovalFigures {
  UnderwritingFeeCollectedRate: number;
  CombinedClosingFeeRate: number;
  PurchasePrice: number | null;
  CombinedClosingFees: number | null;
  UnderwritingFeeCollected: number | null;
  UnderwritingFeeBanked: number | null;
  BankedClosingFees: number | null;
  FirstMonthAdministrationFee: number | null;
}

/** Two-place sum of two rates (4.95 + 2.95 is 7.9, not 7.900000000000001). */
function addRates(a: number, b: number): number {
  return Math.round(Number(((a + b) * 100).toPrecision(15))) / 100;
}

export function computeRecapLetterFigures(input: ConditionalApprovalInputs): RecapLetterFigures {
  const base = computeConditionalApprovalFigures(input);
  const collectedRate = rateOrDefault(
    input.UnderwritingFeeCollectedRate ?? null,
    RECAP_LETTER_DEFAULTS.UnderwritingFeeCollectedRate,
  );
  const combinedRate = addRates(base.OnboardingFeeRate, base.UnderwritingFeeRate);
  const rawBase = input.BaseFinancedAmount;
  if (
    rawBase == null ||
    !Number.isFinite(rawBase) ||
    !(rawBase > 0) ||
    base.OnboardingFee == null ||
    base.UnderwritingFee == null ||
    base.BuyerNotePrincipal == null
  ) {
    return {
      ...base,
      UnderwritingFeeCollectedRate: collectedRate,
      CombinedClosingFeeRate: combinedRate,
      PurchasePrice: null,
      CombinedClosingFees: null,
      UnderwritingFeeCollected: null,
      UnderwritingFeeBanked: null,
      BankedClosingFees: null,
      FirstMonthAdministrationFee: null,
    };
  }
  const printedBase = quantizeCents(rawBase);
  const collected = quantizeCents((printedBase * collectedRate) / 100);
  const banked = quantizeCents(base.UnderwritingFee - collected);
  return {
    ...base,
    UnderwritingFeeCollectedRate: collectedRate,
    CombinedClosingFeeRate: combinedRate,
    PurchasePrice: printedBase,
    CombinedClosingFees: quantizeCents(base.OnboardingFee + base.UnderwritingFee),
    UnderwritingFeeCollected: collected,
    UnderwritingFeeBanked: banked,
    BankedClosingFees: quantizeCents(banked + base.OnboardingFee),
    FirstMonthAdministrationFee: quantizeCents(
      (base.BuyerNotePrincipal * RECAP_LETTER_DEFAULTS.PreContributionAdministrationFeeAnnualPct) / 100 / 12,
    ),
  };
}

/** Owner Recapitalization — the signer fields a recap letter requires before send
 *  (composer_notes 5), detected by the schema: every `required` text field among
 *  the Note Holder / Borrower signer fields. */
export const RECAP_SIGNER_FIELDS = [
  "BorrowerSignerName",
  "BorrowerSignerTitle",
  "NoteHolderSignerName",
  "NoteHolderSignerTitle",
] as const;

/** composer_notes 5 — verbatim. */
export const SIGNER_FIELDS_MISSING_MESSAGE = "Enter the signer's name and title for each entity.";

/** The signer fields this schema declares that are still blank in `valuesJson`
 *  (a SenderDetailsSection's reported JSON). Empty for a schema without them. */
export function missingSignerFields(formSchemaJson: string | null, valuesJson: string | undefined): string[] {
  const schema = parseFormSchema(formSchemaJson);
  const declared = (schema?.fields ?? [])
    .filter((f) => (RECAP_SIGNER_FIELDS as readonly string[]).includes(f.name))
    .map((f) => f.name);
  if (declared.length === 0) return [];
  let values: Record<string, unknown> = {};
  try {
    const parsed = valuesJson ? (JSON.parse(valuesJson) as unknown) : null;
    if (parsed && typeof parsed === "object") values = parsed as Record<string, unknown>;
  } catch {
    values = {};
  }
  return declared.filter((name) => {
    const v = values[name];
    return typeof v !== "string" || v.trim() === "";
  });
}

/** Q19 — the signer capacity lines a recap letter's signature page prints,
 *  "{Entity}, by {Name}, {Title}", from the sender values (the Note Holder on every
 *  recap letter, the Borrower on the recap Borrower letter). A line whose entity,
 *  name or title is blank is omitted, as on the server. */
export function capacityLines(
  fieldNames: readonly string[],
  values: SenderValues,
): Array<{ role: "Note Holder" | "Borrower"; line: string }> {
  const text = (k: string) => {
    const v = values[k];
    return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
  };
  const out: Array<{ role: "Note Holder" | "Borrower"; line: string }> = [];
  if (fieldNames.includes("NoteHolderSignerName")) {
    const [e, n, t] = [text("SellerName"), text("NoteHolderSignerName"), text("NoteHolderSignerTitle")];
    if (e && n && t) out.push({ role: "Note Holder", line: `${e}, by ${n}, ${t}` });
  }
  if (fieldNames.includes("BorrowerSignerName")) {
    const [e, n, t] = [text("BuyerName"), text("BorrowerSignerName"), text("BorrowerSignerTitle")];
    if (e && n && t) out.push({ role: "Borrower", line: `${e}, by ${n}, ${t}` });
  }
  return out;
}

/** The input-side keys the Conditional Approval path writes back when blank or
 *  invalid, so the rate the letter prints is the rate the figures used. */
const CONDITIONAL_APPROVAL_RESOLVED_INPUTS = [
  "OnboardingFeeRate",
  "PaymentAdministrationFeeRate",
  "UnderwritingFeeRate",
  "BalloonTermMonths",
  // Owner Recapitalization — the Option E letters' collected rate.
  "UnderwritingFeeCollectedRate",
] as const;

/** The money tokens the Conditional Approval path computes. */
const CONDITIONAL_APPROVAL_MONEY_TOKENS = [
  "OnboardingFee",
  "BuyerNotePrincipal",
  "MonthlyPayment20",
  "MonthlyPayment10",
  "BalloonPayment20",
  "BalloonPayment10",
  "PaymentAdministrationFee20",
  "PaymentAdministrationFee10",
  "MonthlyDebit20",
  "MonthlyDebit10",
  "UnderwritingFee",
  // Owner Recapitalization (letters.json tokens_new) — written only where the
  // schema declares the field Computed, so today's letters are unchanged.
  "PurchasePrice",
  "CombinedClosingFeeRate",
  "CombinedClosingFees",
  "UnderwritingFeeCollected",
  "UnderwritingFeeBanked",
  "BankedClosingFees",
  "FirstMonthAdministrationFee",
] as const;

function numOrNull(v: SenderValue | undefined): number | null {
  if (!hasVal(v)) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Conditional visibility — mirrors the portal renderer (strict equality).
 *  No rule → visible; `equals` present → exact match; else `in` membership;
 *  else visible. Existing sender templates carry no rule → always visible. */
function isVisible(rule: VisibleWhen | undefined, data: SenderValues): boolean {
  if (!rule) return true;
  if ("equals" in rule && rule.equals !== undefined) {
    return data[rule.field] === rule.equals;
  }
  if (rule.in?.length) {
    return rule.in.includes(data[rule.field] as string | number | boolean);
  }
  return true;
}

/** Compose the human-readable fee-terms sentence from the selected method.
 *  Wording is locked — do not alter (incl. the trailing period). */
function composeFeeTerms(values: SenderValues): string {
  switch (values.FeeMethod) {
    case "flat": {
      const amt = asNumber(values.FlatMonthlyFee);
      return amt > 0
        ? `Flat monthly fee of ${formatCurrency(amt, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} per month.`
        : "";
    }
    case "percentage": {
      const pct = asNumber(values.FeePercentage);
      return pct > 0
        ? `${pct}% of the Regulated Vendor's monthly billings to the Borrower.`
        : "";
    }
    case "custom":
      return String(values.CustomFeeArrangement ?? "").trim().replace(/\s+/g, " ");
    default:
      return "";
  }
}

export function SenderDetailsSection({
  templateId,
  templateName,
  formSchemaJson,
  dealId,
  recipientName,
  staffName,
  staffEmail,
  onValuesChange,
  hidePreview = false,
  initialValues,
  syncKeys,
  syncedValues,
  onSyncedValueChange,
}: Props) {
  const tz = useUserTimeZone();
  const senderFields = useMemo(
    () => parseFormSchema(formSchemaJson)?.fields.filter((f) => f.filledBy === "Sender") ?? [],
    [formSchemaJson],
  );

  const dealQuery = useQuery({
    queryKey: senderDetailsKeys.deal(dealId),
    queryFn: () => dealsApi.get(dealId!),
    enabled: Boolean(dealId),
  });
  const addendumQuery = useQuery({
    queryKey: senderDetailsKeys.addendum(dealId),
    queryFn: () => finalFinancialAddendumApi.get(dealId!),
    enabled: Boolean(dealId),
    // The addendum row often doesn't exist yet at pre-approval time (404);
    // degrade gracefully to blank rather than retrying/erroring loudly.
    retry: false,
  });
  // Financial Addendum — the loan's covenant Calibrated Amounts prefill the 16
  // calibration tokens. Enabled only once the deal resolves a loanId
  // (post-close); pre-close the resolver falls back to the fixed seed defaults.
  const loanId = dealQuery.data?.loanId ?? null;
  const calibrationQuery = useQuery({
    queryKey: senderDetailsKeys.calibration(loanId),
    queryFn: () => covenantCalibrationApi.get(loanId!),
    enabled: Boolean(loanId),
    retry: false,
  });

  // Editable values (System auto-seed + Manual defaults + DealField prefill).
  // Lazily seeded from server-resolved initialValues when supplied (send
  // popup); the seeding effects below don't clobber present values.
  const [values, setValues] = useState<SenderValues>(() => initialValues ?? {});
  const seededSystem = useRef(false);
  const seededDeal = useRef(false);

  // Seed System fields + Manual defaults + recipient prefill once.
  useEffect(() => {
    if (seededSystem.current || senderFields.length === 0) return;
    seededSystem.current = true;
    setValues((prev) => {
      const next = { ...prev };
      for (const f of senderFields) {
        if (f.valueSource === "System") {
          if (f.name === "LetterDate" || f.name === "FinancialAddendumDate")
            next[f.name] = todayIso(tz);
          else if (f.name === "LoanAdministratorName") next[f.name] = staffName ?? "";
          else if (f.name === "LoanAdministratorEmail") next[f.name] = staffEmail ?? "";
          else next[f.name] = "";
        } else if (isEditable(f)) {
          if (f.prefillFrom === "recipient.fullName" && recipientName) {
            next[f.name] = recipientName;
          } else if (f.valueSource === "DealField" && dealId && f.defaultValue != null) {
            // Owner Recapitalization (OP2) — a deal field with a default is
            // prefilled from the deal first; the default is only its fallback
            // (applied by the deal seeding below).
            if (!(f.name in next)) next[f.name] = "";
          } else if (f.defaultValue != null && typeof f.defaultValue !== "boolean") {
            next[f.name] = f.defaultValue as SenderValue;
          } else if (!(f.name in next)) {
            next[f.name] = "";
          }
        }
      }
      return next;
    });
  }, [senderFields, staffName, staffEmail, recipientName, tz, dealId]);

  // Seed DealField values once the deal/addendum/calibration resolve (don't
  // clobber edits).
  useEffect(() => {
    if (seededDeal.current) return;
    if (!dealId) return;
    if (dealQuery.isLoading || addendumQuery.isLoading) return;
    // When the deal has a loan, its covenant calibration prefills the 16
    // Calibrated Amount tokens — wait for that read so we seed the loan's real
    // (possibly-edited) amounts rather than the fixed defaults. A deal with no
    // loan yet (pre-close) leaves the query disabled (never loading).
    if (loanId && calibrationQuery.isLoading) return;
    seededDeal.current = true;
    setValues((prev) => {
      const next = { ...prev };
      for (const f of senderFields) {
        if (f.valueSource !== "DealField") continue;
        const fromDeal = resolveDealToken(
          f.sourceToken, dealQuery.data, addendumQuery.data, calibrationQuery.data,
        );
        // OP2 — a blank deal source falls back to the field's default (the
        // recap letters' interest rate: 10), so a blank rate never prints.
        const resolved =
          fromDeal ??
          (f.defaultValue != null && typeof f.defaultValue !== "boolean"
            ? (f.defaultValue as SenderValue)
            : null);
        if (resolved != null && (next[f.name] == null || next[f.name] === "")) {
          next[f.name] = resolved;
        }
      }
      return next;
    });
  }, [
    dealId,
    loanId,
    dealQuery.isLoading,
    dealQuery.data,
    addendumQuery.isLoading,
    addendumQuery.data,
    calibrationQuery.isLoading,
    calibrationQuery.data,
    senderFields,
  ]);

  // Owner Recapitalization (composer_notes 3, 5) — a value staff edited on the
  // other letter in this send (the Base Financed Amount, the Note Holder signer)
  // is copied here.
  useEffect(() => {
    if (!syncKeys || !syncedValues) return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of syncKeys) {
        if (!(key in syncedValues) || !senderFields.some((f) => f.name === key)) continue;
        if (next[key] !== syncedValues[key]) {
          next[key] = syncedValues[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [syncKeys, syncedValues, senderFields]);

  // Derive the computed fields from the editable inputs.
  const computed = useMemo<SenderValues>(() => {
    // Conditional Approval letters — their own path (Appendix D), selected by the
    // BaseFinancedAmount field; the older letters and forms continue below.
    if (senderFields.some((f) => f.name === "BaseFinancedAmount")) {
      // The recap figures are a superset of the Conditional Approval figures;
      // only the fields a schema declares are written, so today's two letters
      // produce exactly what they did.
      const figs = computeRecapLetterFigures({
        BaseFinancedAmount: numOrNull(values.BaseFinancedAmount),
        InterestRate: numOrNull(values.InterestRate),
        OnboardingFeeRate: numOrNull(values.OnboardingFeeRate),
        PaymentAdministrationFeeRate: numOrNull(values.PaymentAdministrationFeeRate),
        UnderwritingFeeRate: numOrNull(values.UnderwritingFeeRate),
        BalloonTermMonths: numOrNull(values.BalloonTermMonths),
        UnderwritingFeeCollectedRate: numOrNull(values.UnderwritingFeeCollectedRate),
      });
      const out: SenderValues = {};
      for (const f of senderFields) {
        const resolvedInput = (CONDITIONAL_APPROVAL_RESOLVED_INPUTS as readonly string[]).includes(f.name);
        if (resolvedInput && !isComputed(f)) {
          const used = figs[f.name as (typeof CONDITIONAL_APPROVAL_RESOLVED_INPUTS)[number]];
          if (numOrNull(values[f.name]) !== used) out[f.name] = used;
          continue;
        }
        if (!isComputed(f)) continue;
        if ((CONDITIONAL_APPROVAL_MONEY_TOKENS as readonly string[]).includes(f.name)) {
          out[f.name] = figs[f.name as (typeof CONDITIONAL_APPROVAL_MONEY_TOKENS)[number]];
        } else if (f.name === "LoanTerm") {
          out[f.name] = monthsToTermLabel(figs.BalloonTermMonths);
        } else if (f.name === "BalloonDueDate") {
          out[f.name] = balloonDueDate(todayIso(tz), figs.BalloonTermMonths) || null;
        } else {
          out[f.name] = null;
        }
      }
      return out;
    }

    // The Financial Addendum fixes its terms (10% / 240 / 96) in the body and
    // captures no term fields; the pre-approval letter captures them. Fall back
    // to the addendum program terms only for that template (detected by its
    // BuyerNotePrincipal field) so other sender templates are byte-unchanged.
    const isAddendum = senderFields.some((f) => f.name === "BuyerNotePrincipal");
    const principal = asNumber(
      values.LoanAmount ?? values.NotePrincipal ?? values.BuyerNotePrincipal ?? null,
    );
    const annualRatePct = hasVal(values.InterestRate)
      ? asNumber(values.InterestRate)
      : isAddendum ? ADDENDUM_RATE_PCT : 0;
    const amortMonths = hasVal(values.AmortizationMonths)
      ? asNumber(values.AmortizationMonths)
      : isAddendum ? ADDENDUM_AMORT_MONTHS : 0;
    const balloonMonths = hasVal(values.BalloonTermMonths)
      ? asNumber(values.BalloonTermMonths)
      : isAddendum ? ADDENDUM_BALLOON_MONTHS : 0;
    const figs = computeLoanFigures({
      principal,
      annualRatePct,
      amortMonths,
      balloonMonths,
      firstPaymentDateIso: todayIso(tz),
    });
    const out: SenderValues = {};
    for (const f of senderFields) {
      if (!isComputed(f)) continue;
      switch (f.name) {
        case "MonthlyPayment":
          out[f.name] = figs.monthlyPayment;
          break;
        case "BalloonPayment":
          out[f.name] = figs.balloonPayment;
          break;
        case "BalloonDueDate":
          out[f.name] = figs.balloonDueDate || null;
          break;
        case "Amortization":
          out[f.name] = monthsToTermLabel(amortMonths);
          break;
        case "LoanTerm":
          out[f.name] = monthsToTermLabel(balloonMonths);
          break;
        case "FeeTermsDisplay":
          out[f.name] = composeFeeTerms(values);
          break;
        case "MaturityDate": {
          // Closing Date + loan term (96 months for the addendum).
          const closingIso = String(values.ClosingDate ?? "").slice(0, 10);
          out[f.name] = closingIso ? balloonDueDate(closingIso, balloonMonths) : null;
          break;
        }
        case "SegmentPrincipal": {
          // Each of the ten Contribution Segments = 10% of Total Purchase Price.
          const pp = asNumber(values.TotalPurchasePrice);
          out[f.name] = pp > 0 ? Math.round((pp / 10) * 100) / 100 : null;
          break;
        }
        default:
          out[f.name] = null;
      }
    }
    return out;
  }, [values, senderFields, tz]);

  // The full captured value set = editable + computed; report it upward.
  const fullValuesJson = useMemo(
    () => JSON.stringify({ ...values, ...computed }),
    [values, computed],
  );
  useEffect(() => {
    onValuesChange(templateId, fullValuesJson);
  }, [templateId, fullValuesJson, onValuesChange]);

  // Server-rendered PDF preview (== the signed letter). LibreOffice is a slow
  // cold-start that serializes poorly under concurrency, so the preview is
  // rendered ONLY when the user clicks (never auto-on-edit) — at most one
  // render in flight, so a single request reliably finishes under the gateway
  // timeout instead of piling up into a 504.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewStale, setPreviewStale] = useState(false);
  const urlRef = useRef<string | null>(null);

  // Flag the rendered preview as out-of-date when the values change.
  useEffect(() => {
    if (urlRef.current) setPreviewStale(true);
  }, [fullValuesJson]);

  // Revoke the object URL on unmount.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const blob = await collectionPackagesApi.letterPreview(templateId, fullValuesJson);
      const url = URL.createObjectURL(blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setPreviewUrl(url);
      setPreviewStale(false);
    } catch (e) {
      setPreviewError(extractErrorMessage(e));
    } finally {
      setPreviewing(false);
    }
  }, [templateId, fullValuesJson]);

  if (senderFields.length === 0) return null;

  const setField = (name: string, v: SenderValue) => {
    setValues((prev) => ({ ...prev, [name]: v }));
    if (syncKeys?.includes(name)) onSyncedValueChange?.(name, v);
  };

  // Current value set used for conditional-visibility checks (editable + computed).
  const mergedValues: SenderValues = { ...values, ...computed };

  // Q19 / composer_notes 12 — the capacity lines the signature page will print,
  // shown live so staff see the exact text before sending (recap letters only:
  // detected by the signer fields their schemas declare).
  const signerFieldNames = senderFields
    .map((f) => f.name)
    .filter((n) => (RECAP_SIGNER_FIELDS as readonly string[]).includes(n));
  const lines = capacityLines(signerFieldNames, mergedValues);

  return (
    <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
      <h3 className="text-small font-semibold text-onyx m-0">
        Sender details — {templateName}
      </h3>
      <p className="text-caption text-gray-mid m-0">
        Filled by you and pre-filled from the deal where available. The recipient reviews these
        read-only and signs the letter.
      </p>
      <div
        className={
          hidePreview
            ? "flex flex-col gap-space-3"
            : "grid grid-cols-1 lg:grid-cols-2 gap-space-4"
        }
      >
        <div className="flex flex-col gap-space-3">
          {senderFields.map((f) => {
            if (!isVisible(f.visibleWhen, mergedValues)) return null;
            const readOnly = f.valueSource === "System" || isComputed(f);
            const value = readOnly && isComputed(f) ? computed[f.name] : values[f.name];
            if (f.type === "radio") {
              return (
                <FormField key={f.name} label={f.label} helperText={f.helpText}>
                  <div role="radiogroup" aria-label={f.label} className="flex flex-col gap-space-2">
                    {(f.options ?? []).map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-space-2 font-body text-body sm:text-small text-onyx"
                      >
                        <input
                          type="radio"
                          name={f.name}
                          value={opt.value}
                          checked={values[f.name] === opt.value}
                          disabled={readOnly}
                          onChange={() => setField(f.name, opt.value)}
                          className="accent-gold"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </FormField>
              );
            }
            if (f.type === "dropdown") {
              return (
                <FormField key={f.name} label={f.label} helperText={f.helpText}>
                  <Select
                    value={(values[f.name] as string | number | null) ?? ""}
                    disabled={readOnly}
                    onChange={(e) => setField(f.name, e.target.value)}
                  >
                    <option value=""></option>
                    {(f.options ?? []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
              );
            }
            if (f.type === "textarea") {
              return (
                <FormField key={f.name} label={f.label} helperText={f.helpText}>
                  <Textarea
                    value={String(value ?? "")}
                    readOnly={readOnly}
                    disabled={readOnly}
                    onChange={(e) => setField(f.name, e.target.value)}
                  />
                </FormField>
              );
            }
            return (
              <FormField
                key={f.name}
                label={f.label}
                helperText={f.helpText}
                required={f.required === true && !readOnly}
              >
                <Input
                  type={
                    f.type === "currency" || f.type === "number" || f.type === "percentage"
                      ? "number"
                      : f.type === "date"
                        ? "date"
                        : "text"
                  }
                  step={f.type === "currency" || f.type === "percentage" ? "0.01" : undefined}
                  value={value == null ? "" : String(value)}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (f.type === "currency" || f.type === "number" || f.type === "percentage") {
                      setField(f.name, raw === "" ? null : Number(raw));
                    } else {
                      setField(f.name, raw);
                    }
                  }}
                />
              </FormField>
            );
          })}
          {signerFieldNames.length > 0 && (
            <div
              className="flex flex-col gap-space-1 rounded-md border border-border-light bg-white-warm p-space-3"
              data-testid="capacity-lines"
            >
              <span className="text-caption font-medium text-gray-mid">Signature page</span>
              {lines.length === 0 ? (
                <p className="text-caption text-gray-mid italic m-0">{SIGNER_FIELDS_MISSING_MESSAGE}</p>
              ) : (
                lines.map((l) => (
                  <p key={l.role} className="text-small text-onyx m-0">
                    <span className="text-gray-mid">{l.role}: </span>
                    {l.line}
                  </p>
                ))
              )}
            </div>
          )}
        </div>
        {!hidePreview && (
        <div className="flex flex-col gap-space-2">
          <div className="flex items-center justify-between gap-space-2">
            <span className="text-caption font-medium text-gray-mid">Preview</span>
            <div className="flex items-center gap-space-3">
              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-caption text-gold underline"
                >
                  Open / print
                </a>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void runPreview()}
                disabled={previewing}
                loading={previewing}
              >
                {previewUrl ? "Update preview" : "Generate preview"}
              </Button>
            </div>
          </div>
          {previewStale && previewUrl && !previewing && (
            <p className="text-caption text-gray-mid italic m-0">
              Values changed — click "Update preview" to re-render.
            </p>
          )}
          {previewError ? (
            <p className="text-caption text-status-error m-0" role="alert">{previewError}</p>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              title={`Preview — ${templateName}`}
              className="w-full rounded-md border border-border-light bg-white-warm"
              style={{ height: "640px" }}
            />
          ) : (
            <p className="text-caption text-gray-mid italic m-0">
              {previewing
                ? "Rendering preview…"
                : "Click “Generate preview” to render the letter."}
            </p>
          )}
        </div>
        )}
      </div>
    </section>
  );
}
